/**
 * The MCP Servers (governance) tab equivalent of `mcp-connection-status.ts`.
 *
 * The governance list (`GET /api/v1/shops/{slug}/mcp-connections`) used to draw
 * its pill straight from the tool-introspection `status` enum
 * (`connected | error | pending`). That reads a transient introspection hiccup
 * (e.g. an HTTP 504 while re-scanning tools) as a hard red "Error", and it can
 * read a catalog server that was never set up as "Connected" the moment its
 * built-in tools introspect. Neither reflects whether the credential is
 * actually usable.
 *
 * The endpoint now ADDITIVELY carries the same credential signals the Platforms
 * tab already trusts, so the pill can tell the truth about the credential and
 * treat introspection health as a secondary, informational note:
 *
 *   - `credential_present` (bool)      — a credential row exists at all.
 *   - `credential_valid`   (bool|null) — the credential is accepted (not
 *                                        rejected / expired / revoked).
 *   - `credential_active`  (bool|null) — the on/off switch is on.
 *   - `oauth_required`     (bool|null) — OAuth grant is missing or needs
 *                                        re-consent.
 *   - `account_pending`    (bool)      — OAuth is done but no account has been
 *                                        chosen from the picker yet.
 *
 * This helper reuses the Platforms tab vocabulary (`getMcpStatusView`) and its
 * rule for a connection that is both switched off and not valid
 * (`getInactiveInvalidStatus`), so the same credential gets the same label on
 * both tabs. This payload carries no account picker itself; central-api sends
 * `account_pending` instead, so an OAuth setup whose account was never chosen
 * reads Finish Setup here too, and a credential the user switched off reads
 * Deactivated. Without the flag (older payloads) both read Deactivated.
 *
 * When the additive fields are absent (older payloads) or the connection is a
 * user-added `custom` server (which has no catalog credential), this returns
 * `null` so the caller falls back to the raw `status` pill exactly as before.
 */
import {
  McpConnectionStatusView,
  getInactiveInvalidStatus,
  getMcpStatusView,
} from './mcp-connection-status';

/** The subset of an mcp-connections row the governance pill decision needs. */
export interface GovernanceConnectionCredential {
  type?: 'catalog' | 'custom' | string | null;
  credential_present?: boolean | null;
  credential_valid?: boolean | null;
  credential_active?: boolean | null;
  oauth_required?: boolean | null;
  account_pending?: boolean | null;
}

/**
 * The credential-derived pill for a governance connection, using the same
 * label/color vocabulary as the Platforms tab — or `null` when the pill must
 * fall back to the introspection `status` enum (custom connection, or a payload
 * that predates the additive credential fields).
 */
export const getGovernanceConnectionStatusView = (
  conn: GovernanceConnectionCredential | null | undefined
): McpConnectionStatusView | null => {
  if (!conn) return null;

  // Custom (user-added) servers carry no catalog credential — their only truth
  // is the introspection status, so keep the existing pill.
  if (conn.type === 'custom') return null;

  // Older payloads omit the additive fields entirely. `credential_present` is a
  // non-nullable bool in the contract, so its absence is the reliable "no
  // credential signal" test: fall back to the introspection status.
  if (conn.credential_present === undefined) return null;

  // Never set up (e.g. a catalog entry like HubSpot the user never connected):
  // never claim "Connected".
  if (conn.credential_present === false) {
    return getMcpStatusView('not_connected');
  }

  // Switched off. Same rule as the Platforms tab: a switched-off credential
  // reads Deactivated whether or not it still works, unless the OAuth account
  // choice was never finished (Finish Setup).
  if (conn.credential_active === false) {
    return getMcpStatusView(
      conn.credential_valid === false
        ? getInactiveInvalidStatus(conn.account_pending === true)
        : 'deactivated'
    );
  }

  // Switched on but rejected / expired / revoked: reconnect before it works.
  if (conn.credential_valid === false) {
    return getMcpStatusView('reconnect_needed');
  }

  // Switched on and validity unconfirmed: fail closed, never green —
  // matching the Platforms tab's "Status Unknown".
  if (conn.credential_valid !== true || conn.credential_active !== true) {
    return getMcpStatusView('unknown');
  }

  // Works today, but the OAuth grant must be renewed: Action Needed, as on
  // the Platforms tab.
  if (conn.oauth_required === true) {
    return getMcpStatusView('action_needed');
  }

  return getMcpStatusView('connected');
};

/**
 * Which introspection note (if any) a governance card should show, keeping
 * introspection health SECONDARY to credential validity:
 *
 *   - `'hard'` — the card falls back to the raw introspection status (custom
 *                server, or a payload without the additive credential fields),
 *                so a failed re-scan is the real headline: a red error note.
 *   - `'soft'` — the pill is credential-derived, so a failed re-scan is only an
 *                informational note (the credential is fine; its tool list just
 *                could not be refreshed).
 *   - `'none'` — introspection is healthy or there is no error to surface.
 */
export type GovernanceIntrospectionNote = 'none' | 'soft' | 'hard';

export const getGovernanceIntrospectionNote = (
  conn:
    | (GovernanceConnectionCredential & {
        status?: string | null;
        last_error?: string | null;
      })
    | null
    | undefined
): GovernanceIntrospectionNote => {
  if (!conn || conn.status !== 'error' || !conn.last_error) return 'none';
  return getGovernanceConnectionStatusView(conn) ? 'soft' : 'hard';
};
